import { FLAG_CODES } from "./flags.generated.js";
/* DOM construction, formatting, and the shared component vocabulary for
 * the cloud web surfaces. Split out of the old app.js monolith.
 *
 * Response shapes are read through the small normalisers here (pick,
 * asArray, numOrNull) rather than destructured directly, so a field
 * renamed upstream degrades to "unknown" instead of a crash.
 *
 * The state components mirror the desktop's (StatusPill.tsx,
 * EvidencePair.tsx) in structure and wording: same glyphs, same rule that
 * status never rides on hue alone, same refusal to hide counter-evidence.
 * They are re-implemented rather than shared because this surface must run
 * as plain ES modules with no build step.
 */

/* ---------------------------------------------------------------------- dom */

export function el(tag, props = {}, children = []) {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(props)) {
    if (value === null || value === undefined || value === false) continue;
    if (key === "class") node.className = value;
    else if (key === "text") node.textContent = String(value);
    else if (key === "value") node.value = value;
    else if (key === "checked" || key === "disabled" || key === "selected") node[key] = !!value;
    else if (key === "dataset") Object.assign(node.dataset, value);
    else if (key.startsWith("on") && typeof value === "function")
      node.addEventListener(key.slice(2), value);
    else node.setAttribute(key, value === true ? "" : String(value));
  }
  append(node, children);
  return node;
}

export function append(node, children) {
  const list = Array.isArray(children) ? children : [children];
  for (const child of list) {
    if (child === null || child === undefined || child === false) continue;
    if (Array.isArray(child)) append(node, child);
    else if (child instanceof Node) node.appendChild(child);
    else node.appendChild(document.createTextNode(String(child)));
  }
}

const SVG_NS = "http://www.w3.org/2000/svg";

export function svgEl(tag, attrs = {}, children = []) {
  const node = document.createElementNS(SVG_NS, tag);
  for (const [key, value] of Object.entries(attrs)) {
    if (value === null || value === undefined) continue;
    node.setAttribute(key, String(value));
  }
  for (const child of Array.isArray(children) ? children : [children]) {
    if (child) node.appendChild(child);
  }
  return node;
}

export function mount(target, ...children) {
  const node = typeof target === "string" ? document.getElementById(target) : target;
  if (!node) return null;
  node.replaceChildren();
  append(node, children);
  return node;
}

export function show(id, visible) {
  const node = typeof id === "string" ? document.getElementById(id) : id;
  if (node) node.classList.toggle("hidden", !visible);
}

let toastTimer = null;

export function toast(message, kind = "ok") {
  const existing = document.querySelector(".toast");
  if (existing) existing.remove();
  if (toastTimer) clearTimeout(toastTimer);
  const node = el("div", { class: `toast toast-${kind}`, role: "status", text: message });
  document.body.appendChild(node);
  toastTimer = setTimeout(() => node.remove(), 4000);
}

/* ------------------------------------------------------------------ icons
 *
 * The one icon set for every cloud page. 24×24 viewBox, 1.5px stroke,
 * round caps and joins — one construction so a mixed page never carries
 * two visual weights. Everything strokes/fills currentColor, so an icon
 * follows whatever text colour its host has and both themes come free.
 * All decorative: the label text is always adjacent, so the SVG itself
 * is hidden from the accessibility tree.
 */

const ICONS = {
  home: [
    ["path", { d: "M4.5 10.6 12 4.4l7.5 6.2" }],
    ["path", { d: "M6.5 9.8v8.7a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1V9.8" }],
    ["path", { d: "M10.2 19.5v-4.4h3.6v4.4" }],
  ],
  gauge: [
    ["path", { d: "M4.75 16.5a7.25 7.25 0 0 1 14.5 0" }],
    ["path", { d: "M12 16.5l3.5-4.6" }],
    ["circle", { cx: 12, cy: 16.5, r: 1.1, fill: "currentColor", stroke: "none" }],
  ],
};

export function icon(name, size = 16) {
  const parts = ICONS[name];
  if (!parts) return null;
  return svgEl(
    "svg",
    {
      viewBox: "0 0 24 24",
      width: size,
      height: size,
      fill: "none",
      stroke: "currentColor",
      "stroke-width": "1.5",
      "stroke-linecap": "round",
      "stroke-linejoin": "round",
      "aria-hidden": "true",
      focusable: "false",
    },
    parts.map(([tag, attrs]) => svgEl(tag, attrs)),
  );
}

/* The brand mark: a diamond lattice with two crossing helix strands — the
   geometric successor to the "◈" glyph the pages used to carry, so the
   identity survives the upgrade instead of being replaced by it. This is
   the master; the desktop and any page that cannot import this module
   copies the markup and names this file as its source. */
/* A country flag, from the vendored subset.
 *
 * Returns null for a code we do not ship, and the caller renders the
 * two-letter code alone — which is correct on every platform and is what
 * shipped before flags existed. An absent flag costs a decoration, never a
 * fact, which is what makes a curated subset safe.
 *
 * No emoji fallback here, unlike the desktop's Flag.tsx: this runs in
 * whatever browser an operator opened, and a glyph that renders as a flag
 * on the designer's Mac and as two letters on the ISP desk's Windows box
 * is worse than no glyph at all. The desktop can feature-detect because it
 * only ever runs in its own webview.
 */
export function flag(cc, size = 12) {
  if (typeof cc !== "string") return null;
  const code = cc.trim().toLowerCase();
  if (!/^[a-z]{2}$/.test(code) || !FLAG_CODES.has(code)) return null;
  return el("img", {
    class: "flag",
    src: `flags/${code}.svg`,
    alt: "",
    "aria-hidden": "true",
    width: String(Math.round((size * 4) / 3)),
    height: String(size),
    decoding: "async",
  });
}

// The four-path vector mark the product draws outside its reports
// (BrandMark.tsx, the /s/ share page). Until 2026-10-02 this drew a
// different, older diamond-and-crossing mark; web/brand-mark.test.ts holds
// every vector copy to one geometry. The REPORTS carry the raster logo
// instead (operator, 2026-10-02: images/android-chrome-192x192.png), made by
// packaging/report-logo.mjs and pinned by the same test.
export function brandMark(size = 20) {
  const svg = svgEl(
    "svg",
    {
      viewBox: "0 0 24 24",
      width: size,
      height: size,
      fill: "none",
      stroke: "currentColor",
      "stroke-width": "1.5",
      "stroke-linecap": "round",
      "stroke-linejoin": "round",
      "aria-hidden": "true",
      focusable: "false",
    },
    [
      svgEl("path", { d: "M7 3c5.5 3.6 5.5 14.4 0 18" }),
      svgEl("path", { d: "M17 3c-5.5 3.6-5.5 14.4 0 18" }),
      svgEl("path", { d: "M8.8 6.8h6.4" }),
      svgEl("path", { d: "M8.8 17.2h6.4" }),
    ],
  );
  return el("span", { class: "brand-glyph", "aria-hidden": "true" }, [svg]);
}

/* ----------------------------------------------------------- illustrations
 *
 * Small line-art for the panel states, built once and parameterised by
 * kind. Construction rules: ~110px wide, strokes in --muted with exactly
 * one --accent detail, colours as CSS variables in style attributes so a
 * theme switch restyles a rendered illustration without a redraw (same
 * reasoning as chart.js). Decorative — the state's words sit right below,
 * so the SVG is aria-hidden.
 *
 *   empty         — a quiet chart baseline with one accent heartbeat:
 *                   "nothing measured here yet", not "something broke";
 *   error         — an unplugged cable with an accent spark: unmistakably
 *                   a fault, never confusable with having no data;
 *   insufficient  — a half-drawn chart ending in a hollow accent point,
 *                   the dashed remainder unmeasured (§4.2: coarse_loss is
 *                   its own state, not a softer OK).
 */

const STATE_ART = {
  empty: [
    ["path", { d: "M14 28h94M14 40h94", style: "stroke: var(--card-border)" }],
    ["circle", { cx: 10, cy: 52, r: 2.2, style: "stroke: var(--muted)" }],
    ["path", { d: "M14.5 52H62", style: "stroke: var(--muted)" }],
    ["path", { d: "M62 52l5.5-10 5.5 10", style: "stroke: var(--accent)" }],
    ["path", { d: "M73 52h37", style: "stroke: var(--muted)" }],
  ],
  error: [
    ["path", { d: "M6 40h20", style: "stroke: var(--muted)" }],
    ["rect", { x: 26, y: 34.5, width: 11, height: 11, rx: 2.5, style: "stroke: var(--muted)" }],
    ["path", { d: "M37 37.5h6M37 42.5h6", style: "stroke: var(--muted)" }],
    ["rect", { x: 76, y: 32.5, width: 13, height: 15, rx: 3, style: "stroke: var(--muted)" }],
    ["path", { d: "M79 37.5h2.5M79 42.5h2.5", style: "stroke: var(--muted)" }],
    ["path", { d: "M89 40h25", style: "stroke: var(--muted)" }],
    ["path", { d: "M59.5 33v-7M51 36l-6-4.5M68 36l6-4.5", style: "stroke: var(--accent)" }],
  ],
  insufficient: [
    ["path", { d: "M16 12v44h90", style: "stroke: var(--muted)" }],
    ["path", { d: "M16 46c8-1.5 11-13 21-14s11 6 19 5", style: "stroke: var(--muted)" }],
    ["circle", { cx: 58, cy: 36.8, r: 2.6, style: "stroke: var(--accent)" }],
    ["path", { d: "M65 36h41", style: "stroke: var(--muted)", "stroke-dasharray": "2 5", opacity: "0.7" }],
  ],
};

export function stateArt(kind, width = 108) {
  const parts = STATE_ART[kind];
  if (!parts) return null;
  return svgEl(
    "svg",
    {
      class: "state-art",
      viewBox: "0 0 120 72",
      width,
      height: Math.round(width * 0.6),
      fill: "none",
      "stroke-width": "1.5",
      "stroke-linecap": "round",
      "stroke-linejoin": "round",
      "aria-hidden": "true",
      focusable: "false",
    },
    parts.map(([tag, attrs]) => svgEl(tag, attrs)),
  );
}

/* ------------------------------------------------------------ state blocks
 *
 * Four distinct panel states, visually distinct on purpose:
 *   empty         — "nothing here yet", dashed and neutral, no alarm;
 *   error         — "couldn't load", solid down-coloured edge and a pill,
 *                   because an unreachable endpoint must never be mistaken
 *                   for a subscriber having no data;
 *   loading       — skeleton bars, not spinning text;
 *   insufficient  — carried by statusPill("insufficient") and the hatch
 *                   token wherever a window is too small to support a claim.
 */

export function emptyState(title, ...lines) {
  return el("div", { class: "empty" }, [
    stateArt("empty"),
    el("strong", { text: title }),
    ...lines.filter(Boolean).map((line) => el("p", { text: line })),
  ]);
}

/** Error state for a section whose endpoint is not answering. Deliberately
    worded as a gap in the deployment, not as a fault of the viewer — but
    styled as a fault, because "couldn't load" must not look like "nothing
    to show". */
export function unavailableState(what, err) {
  const status = err && Number.isFinite(err.status) ? err.status : 0;
  const detail = status > 0 ? `The API returned ${status}.` : "The API did not respond.";
  return el("div", { class: "empty state-error", role: "alert" }, [
    stateArt("error"),
    statusPill("down", "Couldn’t load"),
    el("strong", { text: `${what} is not available right now` }),
    el("p", { text: detail }),
    el("p", { text: "Nothing has been lost — reload once the API is back." }),
  ]);
}

/** Loading skeleton. role="status" announces once; the visible bars carry
    no text so a half-loaded page never reads as content. */
export function loading(label = "Loading…") {
  return el("div", { class: "loading", role: "status" }, [
    el("div", { class: "skeleton", style: "width: 42%; height: 14px" }),
    el("div", { class: "skeleton", style: "width: 100%; height: 10px" }),
    el("div", { class: "skeleton", style: "width: 63%; height: 10px" }),
    el("span", { class: "visually-hidden", text: label }),
  ]);
}

/* Status pill — the one way ok/degraded/down/unknown/insufficient is
   rendered on this surface. Colour is paired with a glyph and a word so
   the state survives monochrome, colour-blindness and a screen reader;
   "insufficient" additionally carries the hatch (tokens.css) so "not
   enough data" can never be mistaken for a soft OK. */

const STATUS_GLYPH = {
  ok: "●",
  degraded: "◐",
  down: "✕",
  unknown: "◌",
  insufficient: "◌",
};

const STATUS_WORD = {
  ok: "OK",
  degraded: "Degraded",
  down: "Down",
  unknown: "Unknown",
  insufficient: "Not enough data",
};

/** The glyph alone, for a state drawn somewhere a pill does not fit (a
    tile's note, a rating badge). The same map as the pill, so a ◐ means
    one thing wherever this surface prints it. */
export function statusGlyph(status) {
  return STATUS_GLYPH[status in STATUS_GLYPH ? status : "unknown"];
}

export function statusPill(status, label) {
  const s = status in STATUS_GLYPH ? status : "unknown";
  return el("span", { class: `status-pill status-${s}` }, [
    el("span", { class: "status-glyph", "aria-hidden": "true", text: STATUS_GLYPH[s] }),
    label ?? STATUS_WORD[s],
  ]);
}

/* Evidence pair — supporting[] and contradicting[], side by side, both
   always visible. The counter-evidence is NOT behind a disclosure, not
   greyed into a footnote, and an empty contradicting list is rendered as
   the explicit claim "nothing argues against this" — silence and absence
   must not look alike. Any verdict this surface ever renders in full goes
   through this. */
export function evidencePair(supporting, contradicting, opts = {}) {
  const {
    supportingLabel = "Supports this verdict",
    contradictingLabel = "Argues against it",
  } = opts;
  const column = (cls, heading, lines, emptyText) =>
    el("section", { class: `evidence-col ${cls}` }, [
      /* h3, not h4. This pair is always rendered under a section heading —
         an h2 on the NOC portal — so an h4 skipped a level, and a screen
         reader navigating by heading heard the evidence columns as
         subordinate to something that was not there. Measured 2026-09-03
         (portal-noc: H2→H4 at "Supports this reading").

         h3 cannot create the same problem in the other direction: every
         surface that renders a verdict puts it under a section, so the
         parent is an h2 or an h3, and a sibling is not a skip. */
      el("h3", { text: heading }),
      lines.length === 0
        ? el("p", { class: "evidence-empty", text: emptyText })
        : el("ul", {}, lines.map((line) => el("li", { text: String(line) }))),
    ]);
  return el("div", { class: "evidence-pair" }, [
    column("evidence-for", supportingLabel, supporting, "No supporting evidence was recorded."),
    column(
      "evidence-against",
      contradictingLabel,
      contradicting,
      "Nothing was found that argues against this.",
    ),
  ]);
}

/* ------------------------------------------------------------ progress
 *
 * Two honest shapes and nothing else. A determinate bar exists only where
 * a real numerator and denominator exist in the data, and it never
 * replaces the numbers — callers keep the counts as visible text and the
 * bar is illustration beside them (D9). Where no fraction exists (a fetch
 * in flight) the only allowed visual is an indeterminate sweep; a bar that
 * creeps to 90% and waits is a fabricated percentage and does not ship.
 */

/** In-button indeterminate sweep for a request in flight. The sweep is CSS
    (.btn-busy) with its duration derived from a motion token, so
    prefers-reduced-motion stops it without this function knowing why. */
export function btnBusy(button, busy) {
  if (!button) return;
  button.disabled = !!busy;
  button.classList.toggle("btn-busy", !!busy);
  if (busy) button.setAttribute("aria-busy", "true");
  else button.removeAttribute("aria-busy");
}

/** Thin determinate proportion bar. `value` of `max` must both be real
    counts from the payload. With `insufficient: true` the track renders
    fully hatched and no fill is drawn at all — a window too small for a
    rate claim (§4.2) must not be partially filled, because a partial fill
    IS a rate claim. */
export function proportionBar(value, max, opts = {}) {
  const { fill = "var(--accent)", insufficient = false, title, label, minPx = 0 } = opts;
  const track = el("div", {
    class: `bar-track${insufficient ? " bar-insufficient" : ""}`,
    role: "progressbar",
    "aria-valuemin": "0",
    "aria-valuemax": Number.isFinite(max) ? String(max) : null,
    "aria-valuenow": Number.isFinite(value) ? String(value) : null,
    "aria-label": label ?? title,
    title,
  });
  if (!insufficient && Number.isFinite(value) && Number.isFinite(max) && max > 0) {
    const pct = Math.max(0, Math.min(100, (value / max) * 100));
    track.appendChild(
      el("div", {
        class: "bar-fill",
        // min-width keeps a tiny-but-nonzero count visible as a sliver;
        // the exact numbers are always in the adjacent text and title.
        style: `width:${pct.toFixed(3)}%;${value > 0 && minPx > 0 ? `min-width:${minPx}px;` : ""}background:${fill}`,
      }),
    );
  }
  return track;
}

/* --------------------------------------------------------------- formatting */

export function parseTs(value) {
  if (value === null || value === undefined) return NaN;
  if (typeof value === "number") return value < 1e12 ? value * 1000 : value;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? NaN : parsed;
}

export function fmtDateTime(value) {
  const ts = parseTs(value);
  if (!Number.isFinite(ts)) return "—";
  return new Date(ts).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function fmtTimeShort(ts) {
  return new Date(ts).toLocaleString(undefined, {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function fmtAgo(value) {
  const ts = parseTs(value);
  if (!Number.isFinite(ts)) return "never";
  const secs = Math.round((Date.now() - ts) / 1000);
  if (secs < 0) return "just now";
  if (secs < 90) return `${secs}s ago`;
  const mins = Math.round(secs / 60);
  if (mins < 90) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 36) return `${hours} h ago`;
  return `${Math.round(hours / 24)} d ago`;
}

export function fmtDuration(ms) {
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const secs = Math.round(ms / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ${secs % 60}s`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

export function num(value, digits = 1) {
  return Number.isFinite(value) ? value.toFixed(digits) : "—";
}

export function numOrNull(value) {
  const parsed = typeof value === "string" ? Number(value) : value;
  return typeof parsed === "number" && Number.isFinite(parsed) ? parsed : null;
}

export function pick(obj, ...keys) {
  if (!obj || typeof obj !== "object") return undefined;
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null) return obj[key];
  }
  return undefined;
}

/// A human-readable message out of an error response, whatever shape it came
/// in.
///
/// THE API SPEAKS TWO DIALECTS. src/auth/routes.ts sends
/// `{error: {code, message}}`; other modules send `{error: "a_code",
/// message: "..."}`. The console assumed the second everywhere, so a sign-in
/// failure from the first rendered as an object -- `textContent = {…}` prints
/// "[object Object]", which reads as the button having done nothing at all.
/// That is how a wrong password looked like a broken form on the admin panel.
///
/// Prefer the sentence over the code: `message` is written for a person,
/// `code` is written for a switch statement. Falling back to the code beats
/// falling back to nothing, and falling back to the status beats both when
/// the body never arrived.
export function errText(res, fallback) {
  const body = res?.body;
  const e = body?.error;
  const message =
    (typeof e === "object" && e !== null ? e.message || e.code : null) ??
    body?.message ??
    (typeof e === "string" ? e : null);
  if (message) return String(message);
  if (fallback) return fallback;
  return res?.status ? `error ${res.status}` : "error (no response)";
}

export function asArray(payload, ...keys) {
  if (Array.isArray(payload)) return payload;
  for (const key of keys) {
    if (payload && Array.isArray(payload[key])) return payload[key];
  }
  if (payload && Array.isArray(payload.items)) return payload.items;
  if (payload && Array.isArray(payload.data)) return payload.data;
  return [];
}

export async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/* Mark the current page's link in the shared operator nav.
 *
 * The OPERATOR-NAV blocks are byte-identical across the estate pages
 * (pinned by console-layout.test.ts), which means no copy may bake its
 * own aria-current — the site chrome learned that lesson first, and
 * solved it the same way: identical copies, current-ness applied by the
 * page itself. Runs at boot from each page's entry module. */
export function markCurrentNav() {
  const here = location.pathname;
  for (const a of document.querySelectorAll(".topnav a")) {
    if (a.getAttribute("href") === here) a.setAttribute("aria-current", "page");
  }
}
